/**
 * useAreaBestFit - Hook to fetch best-fitting area for map bounds
 *
 * When user pans around the map with border disabled, this hook
 * determines which area (city, neighborhood, etc.) best fits the
 * current map viewport.
 */

import { useQuery } from '@zg-rentals/react-query5';
import { type AreaBestFitResponse, bestFit } from '@/src/lib/api/endpoints/area';
import type { MapBounds } from '@/src/components/features/search/Map/types';

export interface UseAreaBestFitOptions {
  enabled?: boolean;
}

/**
 * Fetch the area that best fits the given map bounds
 *
 * @param bounds - Current map viewport bounds (null to disable)
 * @param options - Query options (enabled, etc.)
 * @returns React Query result with area data
 *
 * @example
 * ```tsx
 * const { data: area, isLoading } = useAreaBestFit(mapBounds);
 * console.log(`Viewing: ${area?.name}`); // "Berkeley, CA"
 * ```
 */
export function useAreaBestFit(bounds: MapBounds | null, options?: UseAreaBestFitOptions) {
  // Validate that bounds contain actual numeric values
  const isValidBounds =
    bounds !== null &&
    typeof bounds.northLat === 'number' &&
    typeof bounds.southLat === 'number' &&
    typeof bounds.eastLng === 'number' &&
    typeof bounds.westLng === 'number' &&
    !isNaN(bounds.northLat) &&
    !isNaN(bounds.southLat) &&
    !isNaN(bounds.eastLng) &&
    !isNaN(bounds.westLng);

  const enabled = options?.enabled !== false && isValidBounds;

  return useQuery<AreaBestFitResponse>({
    queryKey: ['area', 'bestFit', bounds],
    queryFn: ({ signal }) => {
      if (!bounds) throw new Error('Bounds are required for bestFit query');

      // API expects: minLat, maxLat, minLon, maxLon
      return bestFit(
        bounds.southLat, // minLat
        bounds.northLat, // maxLat
        bounds.westLng,  // minLon
        bounds.eastLng,  // maxLon
        signal
      );
    },
    enabled,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes - area doesn't change frequently
    gcTime: 10 * 60 * 1000,   // Keep in cache for 10 minutes after unused
  });
}
